import React, { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { SortPicker } from './SortPicker';
import { DateField } from './DateField';
import { createOrder, errText } from '@/services/tissueJwApi';
import { JobberPicker } from '@/components/jobbers/JobberPicker';

const LineRow = ({ line, idx, onChange, onRemove, canRemove }) => (
  <div className="grid grid-cols-[1fr_120px_36px] gap-2 items-start" data-testid={`jw-line-${idx}`}>
    <div>
      <SortPicker value={line.sort} onPick={(s) => onChange({ ...line, sort: s })} testId={`jw-line-${idx}-sort`} />
      {line.sort && <p className="text-[11px] text-gray-500 mt-1" data-testid={`jw-line-${idx}-fg`}>FG {line.sort.fg_name} · {line.sort.design_name} · {line.sort.free_pcs} pcs free</p>}
    </div>
    <Input type="number" min="1" placeholder="Pieces" value={line.pieces}
      onChange={(e) => onChange({ ...line, pieces: e.target.value })} data-testid={`jw-line-${idx}-pieces`} />
    <Button variant="ghost" size="icon" disabled={!canRemove} onClick={onRemove} data-testid={`jw-line-${idx}-remove`}>
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

export const NewJwRequestDialog = ({ open, onOpenChange, onCreated }) => {
  const [jobber, setJobber] = useState('');
  const [lines, setLines] = useState([{ sort: null, pieces: '' }]);
  const [returnDate, setReturnDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setJobber(''); setLines([{ sort: null, pieces: '' }]); setReturnDate(''); setRemarks(''); };
  const setLine = (i, v) => setLines((ls) => ls.map((l, j) => (j === i ? v : l)));
  const ready = jobber && lines.every((l) => l.sort && Number(l.pieces) > 0);

  const submit = async () => {
    setSaving(true);
    try {
      const o = await createOrder({ jobber_name: jobber, expected_return_date: returnDate || null, remarks,
        lines: lines.map((l) => ({ sort_no: l.sort.sort_no, pieces: Number(l.pieces) })) });
      toast.success(`Request ${o.jw_id} sent to Greige Storage`);
      reset(); onOpenChange(false); onCreated?.(o);
    } catch (e) { toast.error(errText(e, 'Could not raise the request')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="new-jw-request-dialog">
        <DialogHeader>
          <DialogTitle>New job work request</DialogTitle>
          <DialogDescription>Greige Storage will pick bundles of these sorts and transfer them to Tissue for the jobber.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Jobber *</Label><JobberPicker value={jobber} onChange={setJobber} testId="jw-jobber" /></div>
          <div className="space-y-2">
            <Label>Sort no & pieces *</Label>
            {lines.map((l, i) => (
              <LineRow key={i} idx={i} line={l} onChange={(v) => setLine(i, v)} canRemove={lines.length > 1}
                onRemove={() => setLines((ls) => ls.filter((_, j) => j !== i))} />
            ))}
            <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { sort: null, pieces: '' }])} data-testid="jw-add-line-btn">
              <Plus className="w-4 h-4 mr-1" /> Add sort
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Expected return</Label>
              <DateField value={returnDate} onChange={setReturnDate} testId="jw-return-date" /></div>
            <div className="space-y-1.5"><Label>Remarks</Label>
              <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="jw-remarks" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!ready || saving} onClick={submit} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="jw-request-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send to Greige Storage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewJwRequestDialog;
